import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styles from "./UtilityApiKeysConfiguration.module.scss";
import TextInput from "../../../../../common/components/xepp/text-input/TextInput";
import { IntegrationSecretKeysFFIcon } from "../../../../../assets/icons";
import { UtilityApiKeysConfiguration as ApiKeysValues } from "../../../../../model/bps-admin/utility-api-keys";

// Each system's key beside its own vendor ID, one row per system.
const KEY_AND_VENDOR_ID_ROWS: [keyof ApiKeysValues, keyof ApiKeysValues][] = [
	["billingKey", "billingVendorId"],
	["vendingKey", "vendingVendorId"],
];

const KEY_FIELDS: (keyof ApiKeysValues)[] = ["paymentHubKey", "adoraIntegrationsKey"];

interface UtilityApiKeysConfigurationProps {
	control: Control<ApiKeysValues>;
}

/**
 * The utility's integration credentials and its vendor IDs. The form is owned by the page, so it
 * survives a tab switch and is saved with the rest of the page.
 */
export function UtilityApiKeysConfiguration({ control }: Readonly<UtilityApiKeysConfigurationProps>) {
	const { t } = useTranslation("bpsAdmin");

	const renderField = (name: keyof ApiKeysValues) => (
		<div key={name} className={styles.element}>
			<div className={styles.elementText}>
				<span className={styles.elementName}>{t(`utilitySetup.apiKeys.${name}.name`)}</span>
				<span className={styles.elementDescription}>{t(`utilitySetup.apiKeys.${name}.description`)}</span>
			</div>
			<Controller
				control={control}
				name={name}
				render={({ field }) => (
					<TextInput label="" value={field.value} onChange={field.onChange} className={styles.input} />
				)}
			/>
		</div>
	);

	return (
		<section className={styles.card}>
			<header className={styles.header}>
				<span className={styles.headerIcon}>
					<IntegrationSecretKeysFFIcon />
				</span>
				<h4 className={styles.title}>{t("utilitySetup.apiKeys.title")}</h4>
			</header>

			{KEY_AND_VENDOR_ID_ROWS.map(([key, vendorId]) => (
				<div key={key} className={styles.keyAndVendorId}>
					{renderField(key)}
					{renderField(vendorId)}
				</div>
			))}

			{KEY_FIELDS.map(renderField)}
		</section>
	);
}
